import { useState, useCallback, useEffect } from "react";
import { useAppStore } from "../state/store";
import { getBacktestSnapshotForSave, loadBacktestSnapshot } from "../utils/backtestSnapshot";
import * as backtestsApi from "../api/backtests";
import type { SavedBacktestMeta, BacktestSnapshot } from "../state/types";
import { SaveIcon, LoadIcon, DuplicateIcon, ResetIcon } from "./BacktestCardIcons";

const iconButtonClass =
  "p-2 rounded-sm border border-rule bg-newsprint-panel text-ink hover:bg-newsprint-dark focus:outline-none focus:ring-2 focus:ring-ink focus:ring-offset-1";

export default function BacktestCardActions() {
  const currentSavedBacktestId = useAppStore((s) => s.currentSavedBacktestId);
  const currentSavedBacktestName = useAppStore((s) => s.currentSavedBacktestName);
  const currentSavedBacktestGroupLabel = useAppStore((s) => s.currentSavedBacktestGroupLabel);
  const setCurrentSavedBacktest = useAppStore((s) => s.setCurrentSavedBacktest);
  const resetParts = useAppStore((s) => s.resetParts);
  const incrementBacktestListVersion = useAppStore((s) => s.incrementBacktestListVersion);

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveModalName, setSaveModalName] = useState("");
  const [saveModalGroup, setSaveModalGroup] = useState("");
  const [saveModalIsDuplicate, setSaveModalIsDuplicate] = useState(false);
  const [overwriteDialog, setOverwriteDialog] = useState<{
    name: string;
    payload: BacktestSnapshot;
    id: string;
  } | null>(null);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [loadList, setLoadList] = useState<SavedBacktestMeta[]>([]);
  const [loadListLoading, setLoadListLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetSymbol, setResetSymbol] = useState(false);
  const [resetIndicators, setResetIndicators] = useState(false);
  const [resetBacktest, setResetBacktest] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSaveClick = () => {
    setSaveModalName(currentSavedBacktestName ?? "");
    setSaveModalGroup(currentSavedBacktestGroupLabel ?? "");
    setSaveModalIsDuplicate(false);
    setSaveModalOpen(true);
  };

  const handleDuplicateClick = () => {
    setSaveModalName("");
    setSaveModalGroup("");
    setSaveModalIsDuplicate(true);
    setSaveModalOpen(true);
  };

  const handleLoadClick = () => {
    setLoadError(null);
    setLoadModalOpen(true);
  };

  const fetchLoadList = useCallback(async () => {
    setLoadListLoading(true);
    setLoadError(null);
    try {
      const items = await backtestsApi.listBacktests();
      setLoadList(items);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load list";
      setLoadError(msg);
      setLoadList([]);
    } finally {
      setLoadListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadModalOpen) fetchLoadList();
  }, [loadModalOpen, fetchLoadList]);

  const handleSaveModalSubmit = async () => {
    const name = saveModalName.trim();
    if (!name) return;
    const groupLabel = saveModalGroup.trim() || undefined;
    const id = saveModalIsDuplicate ? "new" : (currentSavedBacktestId ?? "new");
    const payload = getBacktestSnapshotForSave(name, groupLabel, id === "new" ? undefined : id);
    setSaving(true);
    try {
      const result = await backtestsApi.saveBacktest(id, payload);
      if (result.conflict) {
        setOverwriteDialog({ name, payload: result.snapshot, id });
        setSaveModalOpen(false);
        return;
      }
      const snap = result.snapshot;
      setCurrentSavedBacktest(snap.id ?? null, snap.name, snap.groupLabel);
      setSaveModalOpen(false);
      incrementBacktestListVersion();
    } catch {
      // Save failed; keep modal open (could add toast/error state)
    } finally {
      setSaving(false);
    }
  };

  const handleOverwriteConfirm = async () => {
    if (!overwriteDialog) return;
    const { name, payload, id } = overwriteDialog;
    setSaving(true);
    try {
      const items = await backtestsApi.listBacktests();
      const other = items.find((b) => b.name === name && b.id !== (currentSavedBacktestId ?? id));
      if (other) await backtestsApi.deleteBacktest(other.id);
      await backtestsApi.saveBacktest(id, payload);
      const snap = payload as BacktestSnapshot;
      setCurrentSavedBacktest(snap.id ?? null, snap.name, snap.groupLabel);
      setOverwriteDialog(null);
      incrementBacktestListVersion();
    } finally {
      setSaving(false);
    }
  };

  const handleLoadFromList = async (meta: SavedBacktestMeta) => {
    setLoadError(null);
    try {
      const snapshot = await backtestsApi.getBacktest(meta.id);
      loadBacktestSnapshot(snapshot);
      setLoadModalOpen(false);
      incrementBacktestListVersion();
    } catch (e) {
      if (e instanceof Error && e.message === "NOT_FOUND") {
        setLoadError("This backtest was deleted or is no longer available.");
        fetchLoadList();
      } else {
        setLoadError(e instanceof Error ? e.message : "Load failed");
      }
    }
  };

  const handleResetClick = () => {
    setResetSymbol(false);
    setResetIndicators(false);
    setResetBacktest(false);
    setResetModalOpen(true);
  };

  const resetOpts = { symbol: resetSymbol, indicators: resetIndicators, backtest: resetBacktest };
  const atLeastOne = resetSymbol || resetIndicators || resetBacktest;

  const handleResetConfirm = () => {
    if (!atLeastOne) return;
    resetParts(resetOpts);
    if (resetBacktest) setCurrentSavedBacktest(null, null);
    setResetModalOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-2" role="group" aria-label="Backtest actions">
        <button
          type="button"
          onClick={handleSaveClick}
          className={iconButtonClass}
          aria-label="Save backtest"
          title="Save backtest"
        >
          <SaveIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={handleLoadClick}
          className={iconButtonClass}
          aria-label="Load backtest"
          title="Load backtest"
        >
          <LoadIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={handleDuplicateClick}
          disabled={currentSavedBacktestName === null}
          className={`${iconButtonClass} disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-label="Create duplicate"
          title="Create duplicate"
        >
          <DuplicateIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={handleResetClick}
          className={iconButtonClass}
          aria-label="Reset workspace"
          title="Reset workspace"
        >
          <ResetIcon className="w-5 h-5" />
        </button>
      </div>

      {saveModalOpen && (() => {
        const isUpdate = !saveModalIsDuplicate && currentSavedBacktestId != null;
        return (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="save-backtest-title"
        >
          <div className="bg-newsprint-panel border border-rule rounded-sm p-8 w-96">
            <h3 id="save-backtest-title" className="text-lg font-semibold mb-4 font-headline">
              {saveModalIsDuplicate ? "Create duplicate" : isUpdate ? "Update backtest" : "Save backtest"}
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveModalSubmit();
              }}
              className="space-y-4"
            >
              <div>
                <label htmlFor="save-backtest-name" className="block text-sm font-medium text-ink mb-1">
                  Name (required)
                </label>
                <input
                  id="save-backtest-name"
                  type="text"
                  value={saveModalName}
                  onChange={(e) => setSaveModalName(e.target.value)}
                  className="w-full px-3 py-2 border border-rule rounded-sm bg-newsprint text-ink focus:outline-none focus:border-ink"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="save-backtest-group" className="block text-sm font-medium text-ink mb-1">
                  Group label (optional)
                </label>
                <input
                  id="save-backtest-group"
                  type="text"
                  value={saveModalGroup}
                  onChange={(e) => setSaveModalGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-rule rounded-sm bg-newsprint text-ink focus:outline-none focus:border-ink"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setSaveModalOpen(false)}
                  className="px-4 py-2 border border-rule rounded-sm text-ink hover:bg-newsprint"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!saveModalName.trim() || saving}
                  className="bg-ink text-newsprint px-4 py-2 rounded-sm hover:opacity-90 disabled:opacity-70"
                >
                  {saving
                    ? (isUpdate ? "Updating…" : "Saving…")
                    : (isUpdate ? "Update" : "Save")}
                </button>
              </div>
            </form>
          </div>
        </div>
        );
      })()}

      {overwriteDialog && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="overwrite-title"
        >
          <div className="bg-newsprint-panel border border-rule rounded-sm p-8 w-96">
            <h3 id="overwrite-title" className="text-lg font-semibold mb-4 font-headline">
              Overwrite?
            </h3>
            <p className="text-sm text-ink mb-4">
              A backtest named &quot;{overwriteDialog.name}&quot; already exists. Overwrite it?
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setOverwriteDialog(null)}
                className="px-4 py-2 border border-rule rounded-sm text-ink hover:bg-newsprint"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOverwriteConfirm}
                disabled={saving}
                className="bg-ink text-newsprint px-4 py-2 rounded-sm hover:opacity-90 disabled:opacity-70"
              >
                {saving ? "Saving…" : "Overwrite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loadModalOpen && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="load-backtest-title"
        >
          <div className="bg-newsprint-panel border border-rule rounded-sm p-8 max-w-md w-full max-h-[80vh] flex flex-col">
            <h3 id="load-backtest-title" className="text-lg font-semibold mb-4 font-headline">
              Load backtest
            </h3>
            <p className="text-sm text-ink mb-2">Choose a saved backtest to load.</p>
            {loadError && (
              <p className="text-sm text-red-600 mb-2" role="alert">
                {loadError}
              </p>
            )}
            {loadListLoading ? (
              <p className="text-sm text-ink/80">Loading…</p>
            ) : loadList.length === 0 ? (
              <p className="text-sm text-ink/80">No saved backtests.</p>
            ) : (
              <ul className="space-y-1 overflow-y-auto flex-1 min-h-0 mb-4">
                {loadList.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => handleLoadFromList(b)}
                      className="w-full text-left px-3 py-2 rounded-sm border border-rule hover:bg-newsprint-dark text-ink text-sm"
                    >
                      <span className="font-medium">{b.name}</span>
                      {b.groupLabel && (
                        <span className="text-ink/70 ml-2">({b.groupLabel})</span>
                      )}
                      {b.updatedAt && (
                        <span className="block text-ink/70 text-xs mt-0.5">
                          {new Date(b.updatedAt).toLocaleString()}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setLoadModalOpen(false)}
                className="px-4 py-2 border border-rule rounded-sm text-ink hover:bg-newsprint"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {resetModalOpen && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-title"
        >
          <div className="bg-newsprint-panel border border-rule rounded-sm p-8 w-96">
            <h3 id="reset-title" className="text-lg font-semibold mb-4 font-headline">
              Reset workspace
            </h3>
            <p className="text-sm text-ink mb-4">
              Choose what to reset. At least one option must be selected.
            </p>
            <div className="space-y-3 mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetSymbol}
                  onChange={(e) => setResetSymbol(e.target.checked)}
                  className="w-4 h-4 rounded border-rule text-ink focus:ring-ink"
                />
                <span className="text-sm font-medium text-ink">Symbol</span>
                <span className="text-ink/70 text-xs">(instruments)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetIndicators}
                  onChange={(e) => setResetIndicators(e.target.checked)}
                  className="w-4 h-4 rounded border-rule text-ink focus:ring-ink"
                />
                <span className="text-sm font-medium text-ink">Indicators</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetBacktest}
                  onChange={(e) => setResetBacktest(e.target.checked)}
                  className="w-4 h-4 rounded border-rule text-ink focus:ring-ink"
                />
                <span className="text-sm font-medium text-ink">Backtest</span>
                <span className="text-ink/70 text-xs">(expression, settings, results)</span>
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setResetModalOpen(false)}
                className="px-4 py-2 border border-rule rounded-sm text-ink hover:bg-newsprint"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetConfirm}
                disabled={!atLeastOne}
                className="bg-ink text-newsprint px-4 py-2 rounded-sm hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
